import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, RefreshCw, Search, ArrowDownToLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { xeroCreditNotesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtMoney(amount, currency) {
  if (amount == null) return '-';
  return `${Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`;
}

const COVERAGE_STATE_LABEL = {
  never_synced: 'Never synced',
  catching_up: 'Catching up',
  steady_state: 'Steady state',
  unmapped_tenant: 'Unmapped tenant',
};

const LINKAGE_STATE_LABEL = {
  allocated: 'Allocated',
  cash_refunded: 'Cash refunded',
  unapplied: 'Unapplied',
  mixed: 'Mixed',
  unresolved_needs_verification: 'Needs verification',
};

const LINKAGE_STATE_COLOR = {
  allocated: 'text-green-600',
  cash_refunded: 'text-blue-600',
  unapplied: 'text-yellow-600',
  mixed: 'text-orange-600',
  unresolved_needs_verification: 'text-red-600',
};

const POLL_INTERVAL_MS = 2000;

const isCreditNoteSyncInProgressError = (err) =>
  err?.response?.status === 409 && err.response?.data?.detail?.status === 'xero_creditnote_sync_in_progress';

export function XeroCreditNotesPanel() {
  const [dispatching, setDispatching] = useState(false);
  const [progress, setProgress] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillApplyResult, setBackfillApplyResult] = useState(null);
  const [backfillPreviewLoading, setBackfillPreviewLoading] = useState(false);
  const [backfillApplyLoading, setBackfillApplyLoading] = useState(false);
  const [backfillError, setBackfillError] = useState(null);

  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await xeroCreditNotesAPI.syncStatus();
        if (data.active) {
          setProgress(data);
          if (data.state === 'running') startPolling();
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await xeroCreditNotesAPI.syncStatus();
        setProgress(data);
        if (!data.active || data.state !== 'running') {
          stopPolling();
          if (data.state === 'done') {
            toast.success(`Credit note sync complete: ${fmtInt(data.credit_notes_synced)} across ${fmtInt(data.tenants_completed)} tenants`);
            loadCard();
          } else if (data.state === 'partial') {
            const parked = (data.partial_tenants || []).join(', ') || 'a tenant';
            toast.warning(`Credit note sync partial: ${parked} still catching up. Run Now again to continue.`);
          } else if (data.state === 'failed') {
            toast.error('Xero credit note sync failed. Check server logs.');
          }
        }
      } catch (err) {
        stopPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling]);

  const runSync = async () => {
    setDispatching(true);
    setProgress(null);
    try {
      await xeroCreditNotesAPI.sync();
      startPolling();
    } catch (err) {
      if (isCreditNoteSyncInProgressError(err)) {
        toast.info('A Xero credit note sync is already in progress.');
        try {
          const { data } = await xeroCreditNotesAPI.syncStatus();
          setProgress(data);
          if (data.active && data.state === 'running') startPolling();
        } catch {
          // Best-effort.
        }
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setDispatching(false);
    }
  };

  const runCoverage = async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const { data } = await xeroCreditNotesAPI.tenantCoverage();
      setCoverage(data);
    } catch (err) {
      setCoverageError(getErrorMessage(err));
    } finally {
      setCoverageLoading(false);
    }
  };

  const runBackfillPreview = async () => {
    setBackfillPreviewLoading(true);
    setBackfillApplyResult(null);
    setBackfillError(null);
    try {
      const { data } = await xeroCreditNotesAPI.previewBackfill();
      setBackfillPreview(data);
    } catch (err) {
      setBackfillError(getErrorMessage(err));
    } finally {
      setBackfillPreviewLoading(false);
    }
  };

  const runBackfillApply = async () => {
    if (!window.confirm(
      `Reset ${backfillPreview?.count || 0} tenant(s) to a full historical re-walk of credit notes? ` +
      'Never duplicates (idempotent upsert on external_id).'
    )) return;
    setBackfillApplyLoading(true);
    setBackfillError(null);
    try {
      const { data } = await xeroCreditNotesAPI.applyBackfill();
      setBackfillApplyResult(data);
      runCoverage();
    } catch (err) {
      setBackfillError(getErrorMessage(err));
    } finally {
      setBackfillApplyLoading(false);
    }
  };

  const loadCard = useCallback(async () => {
    setCardLoading(true);
    setCardError(null);
    try {
      const { data } = await xeroCreditNotesAPI.card();
      setCard(data);
    } catch (err) {
      setCardError(getErrorMessage(err));
    } finally {
      setCardLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCard();
  }, [loadCard]);

  const busy = dispatching || polling;
  const tenants = progress?.tenants || {};
  const tenantList = Object.entries(tenants);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Xero Credit Notes (ACCRECCREDIT / ACCPAYCREDIT)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Pulls credit notes from every connected Xero tenant into a standalone collection
          (never merged with invoices). Allocations against invoices, and remaining/refunded
          amounts, are read verbatim from Xero -- linkage state is never inferred from a computed
          gap; anything that doesn't cleanly reconcile is flagged "Needs verification" instead of
          guessed. Also runs on a 6-hour schedule.
        </p>
        <div className="flex gap-2">
          <Button onClick={runSync} disabled={busy} size="sm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Run Now
          </Button>
          <Button variant="outline" onClick={loadCard} disabled={cardLoading} size="sm">
            {cardLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh card
          </Button>
        </div>

        {progress && progress.active && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge className={`text-[10px] ${
                progress.state === 'done'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : progress.state === 'partial'
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                  : progress.state === 'failed'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
              }`}>
                {progress.state === 'done' ? 'COMPLETE'
                  : progress.state === 'partial' ? 'PARTIAL'
                  : progress.state === 'failed' ? 'FAILED'
                  : 'RUNNING'}
              </Badge>
              {progress.state === 'running' && (
                <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>

            {progress.tenants_total > 0 && (
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Tenants: {fmtInt(progress.tenants_completed)} / {fmtInt(progress.tenants_total)}</span>
                  <span>Credit notes synced: {fmtInt(progress.credit_notes_synced)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (progress.tenants_completed / progress.tenants_total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {tenantList.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Tenant</th>
                      <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                      <th className="text-right px-2 py-1.5 font-medium">Synced</th>
                      <th className="text-left px-2 py-1.5 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantList.map(([name, t]) => (
                      <tr key={name} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono text-[10px]">{name}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">
                          {t.state === 'unresolved'
                            ? <Badge variant="destructive" className="text-[10px]">UNRESOLVED</Badge>
                            : (t.entity_id || '-')}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(t.count)}</td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className={`text-[10px] ${
                            t.state === 'done' ? 'text-green-600'
                            : t.state === 'partial' ? 'text-orange-600'
                            : t.state === 'error' || t.state === 'unresolved' ? 'text-red-600'
                            : 'text-yellow-600'
                          }`}>
                            {t.state}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {progress.errors && progress.errors.length > 0 && (
              <div className="text-xs text-red-600 space-y-1">
                {progress.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}

        <hr className="border-muted" />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Tenant coverage</p>
            <Button variant="outline" size="sm" onClick={runCoverage} disabled={coverageLoading}>
              {coverageLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Check coverage
            </Button>
          </div>
          {coverageError && <p className="text-xs text-red-600">{coverageError}</p>}
          {coverage && !coverageError && (
            coverage.xero_connected ? (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Tenant</th>
                      <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                      <th className="text-left px-2 py-1.5 font-medium">Coverage</th>
                      <th className="text-left px-2 py-1.5 font-medium">Last synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(coverage.tenants || []).map((t) => (
                      <tr key={t.tenant_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono text-[10px]">{t.tenant_name}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">
                          {t.coverage_state === 'unmapped_tenant'
                            ? <Badge variant="destructive" className="text-[10px]">UNMAPPED</Badge>
                            : (t.entity_id || '-')}
                        </td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className={`text-[10px] ${
                            t.coverage_state === 'steady_state' ? 'text-green-600'
                            : t.coverage_state === 'unmapped_tenant' ? 'text-red-600'
                            : 'text-yellow-600'
                          }`}>
                            {COVERAGE_STATE_LABEL[t.coverage_state] || t.coverage_state}
                            {t.force_backfill ? ' (backfill queued)' : ''}
                          </Badge>
                        </td>
                        <td className="px-2 py-1 text-[10px] text-muted-foreground">{t.last_synced_at || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Xero is not connected.</p>
            )
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Backfill window</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={runBackfillPreview} disabled={backfillPreviewLoading}>
                {backfillPreviewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ArrowDownToLine className="h-3 w-3 mr-1" />}
                Preview
              </Button>
              {backfillPreview && !backfillPreview.error && backfillPreview.count > 0 && (
                <Button
                  variant="default" size="sm" onClick={runBackfillApply}
                  disabled={backfillApplyLoading || !!backfillApplyResult}
                >
                  {backfillApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Apply ({backfillPreview.count})
                </Button>
              )}
            </div>
          </div>
          {backfillError && <p className="text-xs text-red-600">{backfillError}</p>}
          {backfillPreview && !backfillError && (
            <p className="text-xs">{backfillPreview.count} tenant(s) would be reset to a full historical re-walk.</p>
          )}
          {backfillApplyResult && !backfillError && (
            <p className="text-xs text-green-700 dark:text-green-400">
              Queued: {(backfillApplyResult.applied || []).join(', ') || 'none'}
            </p>
          )}
        </div>

        <hr className="border-muted" />

        <div className="space-y-3">
          <p className="text-xs font-medium">Credit notes card</p>
          {cardError && <p className="text-xs text-red-600">{cardError}</p>}
          {card && !cardError && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="border rounded-lg p-2">
                  <p className="text-muted-foreground">Total</p>
                  <p className="text-lg font-semibold tabular-nums">{fmtInt(card.total_count)}</p>
                </div>
                {Object.entries(card.by_linkage_state || {}).map(([state, count]) => (
                  <div className="border rounded-lg p-2" key={state}>
                    <p className={`text-muted-foreground ${LINKAGE_STATE_COLOR[state] || ''}`}>
                      {LINKAGE_STATE_LABEL[state] || state}
                    </p>
                    <p className="text-lg font-semibold tabular-nums">{fmtInt(count)}</p>
                  </div>
                ))}
              </div>

              {card.unresolved_needs_verification && card.unresolved_needs_verification.length > 0 && (
                <div className="rounded p-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300">
                  {card.unresolved_needs_verification.length} credit note(s) don't cleanly reconcile
                  (allocated + refunded + remaining != total) -- needs manual verification, never guessed.
                </div>
              )}

              {card.rows && card.rows.length > 0 && (
                <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Credit note</th>
                        <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                        <th className="text-left px-2 py-1.5 font-medium">Contact</th>
                        <th className="text-left px-2 py-1.5 font-medium">Direction</th>
                        <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                        <th className="text-right px-2 py-1.5 font-medium">Remaining</th>
                        <th className="text-left px-2 py-1.5 font-medium">Linkage</th>
                        <th className="text-left px-2 py-1.5 font-medium">Allocations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.rows.map((r) => (
                        <tr key={r.id} className="border-b last:border-0 align-top">
                          <td className="px-2 py-1 font-mono text-[10px]">{r.credit_note_number}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{r.entity_id || '-'}</td>
                          <td className="px-2 py-1 text-[10px]">{r.supplier_client_name}</td>
                          <td className="px-2 py-1 text-[10px]">{r.direction}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(r.amount, r.currency)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(r.remaining_credit, r.currency)}</td>
                          <td className="px-2 py-1">
                            <Badge variant="outline" className={`text-[10px] ${LINKAGE_STATE_COLOR[r.linkage_state] || ''}`}>
                              {LINKAGE_STATE_LABEL[r.linkage_state] || r.linkage_state}
                            </Badge>
                          </td>
                          <td className="px-2 py-1 text-[10px]">
                            {(r.allocations || []).length === 0 ? '-' : (
                              <ul className="space-y-0.5">
                                {r.allocations.map((a, i) => (
                                  <li key={i}>{a.invoice_number || a.invoice_id || '?'}: {fmtMoney(a.amount, r.currency)}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
